import { closeIconV2, infoIconV2 } from '@/shared/ui/assets';
import { CustomButton, LoadingDrawer } from '@/shared/ui/components';
import { Drawer } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import '@/shared/ui/components/drawers/style.scss';
import { useDeviceInfoDrawer } from './useDeviceInfoDrawer';
import type { DeviceInfoDrawerProps } from './deviceInfoDrawer.types';
import './deviceInfoDrawer.scss';

/**
 * Side drawer showing the details of a single device. Loads its sections through the drawer hook and renders a titled 500px drawer with a close button, a high-sampling-rate info line and an end-of-life warning when those flags are set, mapping the loaded sections into header and label-value rows and adding a shadow to the title once the body is scrolled.
 *
 * @param visible - Whether the drawer is open.
 * @param handleClickCloseButton - Callback closing the drawer.
 * @param deviceId - Device whose details are shown.
 * @returns The device info drawer element.
 */
const DeviceInfoDrawer = ({ visible, handleClickCloseButton, deviceId }: DeviceInfoDrawerProps) => {
  const { state, handleScroll } = useDeviceInfoDrawer({ visible, deviceId });

  const closeButton = (
    <CustomButton
      id="close-btn"
      style="neutral"
      type="tertiary"
      size="small"
      icon={<ReactSVG src={closeIconV2} />}
      onClick={handleClickCloseButton}
      ariaLabel={t`Close`}
    />
  );

  return (
    <Drawer
      id="device-info-drawer-id"
      aria-label={`${t`Device`} ${deviceId}`}
      rootClassName="custom-drawer device-info-drawer"
      width={500}
      closable={false}
      onClose={handleClickCloseButton}
      open={visible}
    >
      <LoadingDrawer isLoading={state.isLoading} rightIcon={closeButton}>
        <div
          className={classNames('drawer-title-container', state.isScroll ? '--has-box-shadow' : '')}
        >
          <span className="drawer-title">{`${t`Device`} ${deviceId}`}</span>
          {closeButton}
        </div>

        <div className="drawer-body-container" onScroll={handleScroll}>
          {state.isHighSamplingRate && (
            <div className="info-line">
              <img src={infoIconV2} alt="Info Icon" />
              {t`This device is operating at a high sampling rate.`}
            </div>
          )}
          {state.isEOL && (
            <div className="danger-line">{t`This device has reached its end of life.`}</div>
          )}
          <div>
            {_.map(state.data, (element, index) => (
              <div key={index} className="drawer-body">
                {!!element.header && (
                  <div className="drawer-header">
                    <span>{element.header}</span>
                  </div>
                )}
                <div className="drawer-content">
                  {_.map(
                    _.filter(element.content, (x) => !_.isEmpty(x)),
                    (item, index) => (
                      <div key={index} className="drawer-item">
                        <span>{`${item.label}:`}</span>
                        <span>{item.value}</span>
                      </div>
                    ),
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="additional-div" />
        </div>
      </LoadingDrawer>
    </Drawer>
  );
};

export default DeviceInfoDrawer;
